import Vue from 'vue';
import { getBlockById } from '../../blocks/getBlockById.js';
import { BlockSelectionState } from '../../blocks/_models/blockSelectionState.js';
import { findParentBlockInBlocks } from '../../blocks/findParentBlockInBlocks.js';
import { buildSelectionTree } from './buildSelectionTree.js';

function getBlockIdByState(selectionTreeBranch, state) {
	for (const selectionTreeNode of selectionTreeBranch) {
		if (state === selectionTreeNode.state) {
			return selectionTreeNode.id;
		} else if (selectionTreeNode.hasOwnProperty('children')) {
			let blockId = getBlockIdByState(selectionTreeNode.children, state);
			if (blockId) return blockId;
		}
	}
}

export function createBlocksSelector(vm) {
	return new Vue({
		data() {
			return {
				selectionTree: [],
				blocks: vm.blocks,
				overlaysQueue: [],
				overlayVariables: null,
				firstOverlayParent: null
			};
		},
		computed: {
			overlayActive() {
				return this.overlaysQueue.at(-1);
			}
		},
		watch: {
			blocks: {
				immediate: true,
				deep: true,
				handler() {
					this.selectionTree = buildSelectionTree(this.blocks);
				}
			}
		},
		methods: {
			setBlockName(id, name) {
				const block = getBlockById(this.blocks, id);
				if (block) block.name = name;
			},
			findParentBlock(blockId) {
				return findParentBlockInBlocks(blockId, this.blocks);
			},
			getBlockByState(state) {
				const foundBlockId = getBlockIdByState(this.selectionTree, state);
				if (!foundBlockId) return null;
				return getBlockById(this.blocks, foundBlockId);
			},
			$updateState(selectionTreeBranch, state, selectedNodeId) {
				for (let currentNode of selectionTreeBranch) {
					if (selectedNodeId === undefined || currentNode.id === selectedNodeId) {
						currentNode.state = state;
						this.$emit(`state-changed:${currentNode.id}`, state);

						if (currentNode.id === selectedNodeId) {
							const selectedBlock = getBlockById(this.blocks, selectedNodeId);
							if (selectedBlock) {
								this.$emit('selection-changed', { block: selectedBlock, state });
							}
						} else if (currentNode.children) {
							if (state === BlockSelectionState.primarySelected || state === BlockSelectionState.secondarySelected) {
								this.$updateState(currentNode.children, BlockSelectionState.secondarySelected);
							} else if (state === BlockSelectionState.notSelected) {
								this.$updateState(currentNode.children, BlockSelectionState.notSelected);
							} else if (state === BlockSelectionState.editContent) {
								this.$updateState(currentNode.children, BlockSelectionState.notSelected);
							}
						}
					} else if (currentNode.children && currentNode.children.length > 0) {
						this.$updateState(currentNode.children, state, selectedNodeId);
					}
				}
			},
			selectionAdd(id) {
				// Parse tree and if block.id == id
				// Change block.state = BlockSelectionState.primarySelected to
				// for all children and subchildren block.children[].state= BlockSelectionState.secondarySelected
				this.$setParentsAsEdit(id);
				this.$updateState(this.selectionTree, BlockSelectionState.primarySelected, id);
			},
			selectionEdit(id) {
				// Parse tree and if block.id == id
				// Change block.state = BlockSelectionState.editContent to
				// for all children and subchildren block.children[].state= BlockSelectionState.secondarySelected
				this.$setParentsAsEdit(id);
				this.$updateState(this.selectionTree, BlockSelectionState.editContent, id);
			},
			$setParentsAsEdit(id) {
				// set BlockSelectionState.editContent to all block's parent starting from the last one first
				var parent = this.findParentBlock(id);
				if (parent) {
					this.selectionEdit(parent.id);
				}
			},
			selectionClear() {
				// Parse all tree and set block.state = BlockSelectionState.notSelected

				this.$updateState(this.selectionTree, BlockSelectionState.notSelected);
				this.$emit('selection-changed', { block: null });
			},
			selectParentBlock(id) {
				const parentBlock = this.findParentBlock(id);
				if (!parentBlock) {
					this.selectionClear();
				} else {
					this.selectionEdit(parentBlock.id);
				}
			},
			openNewOverlay(newOverlay, variables) {
				if (this.overlaysQueue.some(overlay => overlay.id === newOverlay.id)) return;

				this.overlaysQueue.push({
					id: newOverlay.id,
					parent: newOverlay.parent,
					config: newOverlay,
					variables: variables || this.overlayVariables
				});

				this.overlayVariables = variables;
				if (!this.firstOverlayParent) this.firstOverlayParent = newOverlay.parent;
			},
			closeOverlay(closeAll) {
				if (closeAll) {
					this.closeAllOverlays();
					return;
				}

				this.$closeCurrentOverlay();
			},
			closeAllOverlays() {
				if (this.overlaysQueue.length === 0) return;

				this.$updateState(this.selectionTree, BlockSelectionState.primarySelected, this.firstOverlayParent);

				this.overlaysQueue = [];
				this.overlayVariables = null;
				this.firstOverlayParent = null;
			},
			$closeCurrentOverlay() {
				const { id, parent } = this.overlayActive;

				this.overlaysQueue = this.overlaysQueue.filter(overlay => overlay.id !== id);
				this.overlayVariables = this.overlayActive ? this.overlayActive.variables : null;

				this.$updateState(this.selectionTree, BlockSelectionState.primarySelected, parent);
			},
			updateOverlayVariablesOnBlockState(blockId, variables) {
				this.$on(`state-changed:${blockId}`, state => {
					if (state === 'not-selected') {
						this.overlayVariables = null
						return
					}
					
					this.overlayVariables = variables
				})
			}
		}
	});
}
